import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import "./dashboard.scss";
import {
  PillStats,
  statsData,
  currentOpenCount,
  CurrentOpenCountPill,
  generateColorMap,
} from "./MDRCommonComponents";
import { DateRangeSelector } from "../SysGuard/SysGuardCommonComp";
import { useDispatch, useSelector } from "react-redux";
import {
  GET_MDR_DASHBOARD_ALL,
  SET_MDR_DASHBOARD_FROM_TIME_STAMP,
  SET_MDR_DASHBOARD_TO_TIME_STAMP,
} from "./mdrConstants";
import { isNull } from "lodash";
import {
  getBarChartOptions,
  getLineChartOptions,
  getDonutChartOptions,
  getRadarChartOptions,
} from "./chartConfig";
import IncidentList from "./IncidentList/IncidentList";
import JiraIncidentTable from "./JiraIncidentTable";
import {
  Grid,
  makeStyles,
  Paper,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from "material-ui-core";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { type } from "@amcharts/amcharts4/core";
import IncidentChartFilter from "./IncidentList/IncidentChartFilter";
import { NO_DATA_TO_SHOW_RP } from "../../components/views/RiskPilot/riskPilotConstant";
import noDataAvailableSysguard from "../../assets/icons/noDataAvailableSysguard.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    marginTop: 0,
  },
  paper: {
    paddingRight: theme.spacing(1.3),
    paddingLeft: theme.spacing(1.3),
    paddingBottom: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    boxShadow: "none",
  },
}));

const Dashboard = () => {
  const barRef = useRef(null);
  const lineRef = useRef(null);
  const donutRef = useRef(null);
  const radarRef = useRef(null);

  // Store echarts instances
  const chartInstancesRef = useRef([]);

  const dispatch = useDispatch();
  const classes = useStyles();
  const isSmallQuery = useMediaQuery("(max-width:1800px)");
  const isSmall = isSmallQuery;

  const fromTimeStamp = useSelector((state) => state.mdrReducer.fromTimeStamp);
  const toTimeStamp = useSelector((state) => state.mdrReducer.toTimeStamp);
  const dashboardOverviewCount = useSelector(
    (state) => state.mdrReducer.mdrDashboardOverviewCount
  );
  const mdrDashboardIncidentSummary = useSelector(
    (state) => state.mdrReducer.mdrDashboardIncidentSummary
  );
  const selectedIncident = useSelector(
    (state) => state.mdrReducer.selectedIncident
  );

  const loader = useSelector((state) => state.mdrReducer.loader);

  const [expandedChart, setExpandedChart] = useState(null);
  const [excludeSIEM, setExcludeSIEM] = useState(false);

  const toggleExpand = (chartKey) => {
    setExpandedChart((prev) => (prev === chartKey ? null : chartKey));
  };

  // Fetch dashboard data when date changes
  useEffect(() => {
    if (!isNull(fromTimeStamp) && !isNull(toTimeStamp)) {
      dispatch({ type: GET_MDR_DASHBOARD_ALL });
    }
  }, [fromTimeStamp, toTimeStamp, dispatch]);

  // Initialize charts
  useEffect(() => {
    if (!mdrDashboardIncidentSummary || mdrDashboardIncidentSummary?.length === 0) return;
    const charts = [
      { ref: barRef, option: getBarChartOptions({ isSmall }) },
      { ref: lineRef, option: getLineChartOptions({ isSmall }) },
      { ref: donutRef, option: getDonutChartOptions({ isSmall }) },
      { ref: radarRef, option: getRadarChartOptions({ isSmall }) },
    ];

    chartInstancesRef.current = charts.map(({ ref, option }) => {
      const chart = echarts.init(ref.current);
      chart.setOption(option);
      return chart;
    });

    const handleResize = () =>
      chartInstancesRef.current.forEach((chart) => chart.resize());
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstancesRef.current.forEach((chart) => chart.dispose());
    };
  }, [isSmall, excludeSIEM, mdrDashboardIncidentSummary]);

  useEffect(() => {
    const selectedSummaries =
      !selectedIncident || selectedIncident.length === 0
        ? mdrDashboardIncidentSummary
        : mdrDashboardIncidentSummary.filter((summary) =>
            selectedIncident.includes(summary.type)
          );

    if (!selectedSummaries || selectedSummaries.length === 0) return;

    // All unique x-axis labels
    const merged = {};

    selectedSummaries.forEach((s) => {
      s.xAxis.forEach((x) => {
        const startDate = x.toolTip.match(/\((\d{4}-\d{2}-\d{2})/)[1]; // first date
        if (!merged[startDate]) {
          merged[startDate] = { label: x.label, toolTip: x.toolTip };
        }
      });
    });

    const allLabels = Object.values(merged)
      .sort(
        (a, b) =>
          new Date(a.toolTip.match(/\((\d{4}-\d{2}-\d{2})/)[1]) -
          new Date(b.toolTip.match(/\((\d{4}-\d{2}-\d{2})/)[1])
      )
      .map((x) => x.label);

    // Generate color map based on summary types
    const summaryTypes = [
      ...new Set(selectedSummaries.map((summary) => summary.type)),
    ];
    const colorMap = generateColorMap(summaryTypes);

    // --- BAR CHART ---
    const barSeries = selectedSummaries.map((summary) => {
      const labelToCount = summary.xAxis.reduce((acc, item, i) => {
        acc[item.label] = summary.counts[i] ?? 0;
        return acc;
      }, {});
      return {
        name: summary.type,
        type: "bar",
        stack: "total",
        data: allLabels.map((label) => labelToCount[label] ?? 0),
        color: colorMap[summary.type] || "#ccc",
      };
    });
    const barOptions = getBarChartOptions({
      xAxis: allLabels,
      series: barSeries,
      isSmall,
    });

    // --- LINE CHART ---
    const mergedOpen = new Array(allLabels.length).fill(0);
    const mergedTruePositive = new Array(allLabels.length).fill(0);
    const mergedFalsePositive = new Array(allLabels.length).fill(0);
    const mergedOther = new Array(allLabels.length).fill(0);
    selectedSummaries.forEach((summary) => {
      summary.open.forEach((count, idx) => {
        mergedOpen[idx] += count ?? 0;
      });
      summary.truePositive.forEach((count, idx) => {
        mergedTruePositive[idx] += count ?? 0;
      });
      summary.falsePositive.forEach((count, idx) => {
        mergedFalsePositive[idx] += count ?? 0;
      });
      summary.other.forEach((count, idx) => {
        mergedOther[idx] += count ?? 0;
      });
    });
    const lineOptions = getLineChartOptions({
      xAxis: allLabels.map((x) => x),
      xAxisWithTooltip: allLabels,
      open: mergedOpen,
      truePositive: mergedTruePositive,
      falsePositive: mergedFalsePositive,
      other: mergedOther,
    });

    // --- DONUT CHART ---
    const statusMap = {};
    selectedSummaries.forEach((summary) => {
      summary.status.forEach((stat) => {
        statusMap[stat.type] = (statusMap[stat.type] || 0) + stat.count;
      });
    });
    const combinedStatus = Object.keys(statusMap).map((type) => ({
      type,
      count: statusMap[type],
    }));
    const donutOptions = getDonutChartOptions({
      status: combinedStatus,
      isSmall,
    });

    // --- RADAR CHART ---
    const detectionMap = {};
    selectedSummaries.forEach((summary) => {
      summary.detectionMethod.forEach((dm) => {
        detectionMap[dm.type] = (detectionMap[dm.type] || 0) + dm.count;
      });
    });
    const combinedDetection = Object.keys(detectionMap).map((type) => ({
      type,
      count: detectionMap[type],
    }));
    const radarOptions = getRadarChartOptions({
      detectionMethod: combinedDetection,
      isSmall,
      excludeSIEM,
    });

    // --- UPDATE CHARTS ---
    chartInstancesRef.current[0]?.setOption(barOptions, true);
    chartInstancesRef.current[1]?.setOption(lineOptions, true);
    chartInstancesRef.current[2]?.setOption(donutOptions, true);
    chartInstancesRef.current[3]?.setOption(radarOptions, true);
  }, [selectedIncident, mdrDashboardIncidentSummary, isSmall, excludeSIEM]);

  useEffect(() => {
    // First quick resize so it doesn't look broken mid-way
    chartInstancesRef.current.forEach((chart) => chart.resize());

    // Then final resize after the animation duration
    const timeout = setTimeout(() => {
      chartInstancesRef.current.forEach((chart) => chart.resize());
    }, 300); // match your CSS transition duration

    return () => clearTimeout(timeout);
  }, [expandedChart]);

  // Merge API values into statsData
  const updatedStatsData = statsData.map((item) => {
    const apiKey = item.label === "Total Incidents" ? "Total" : item.label;
    return {
      ...item,
      value: dashboardOverviewCount?.[apiKey] ?? item.value,
    };
  });

  const updateCurrentCount = currentOpenCount.map((item) => {
    return {
      ...item,
      value: dashboardOverviewCount?.["OpenCount"] ?? item.value,
    };
  });

  return (
    <div className="mdr-dashboard-parent-container">
      {loader && (
        <>
          <div className="mainLoader-backdrop"></div>
          <img src={Page_Loader} alt="loading" className="mainLoader" />
        </>
      )}
      <div className="dashboard-header">
        <div className="dashboard-left-container">
          <div className="dashboard-title">Dashboard</div>
          <div>
            <span className="date-range-label">Date Range</span>
            <DateRangeSelector
              fromTimeStamp={fromTimeStamp}
              toTimeStamp={toTimeStamp}
              fromAction={(payload) => ({
                type: SET_MDR_DASHBOARD_FROM_TIME_STAMP,
                payload,
              })}
              toAction={(payload) => ({
                type: SET_MDR_DASHBOARD_TO_TIME_STAMP,
                payload,
              })}
              loc="MDRDashboard"
            />
          </div>
        </div>
        <CurrentOpenCountPill data={updateCurrentCount} />
        <PillStats data={updatedStatsData} />
      </div>
      {mdrDashboardIncidentSummary && mdrDashboardIncidentSummary.length > 0 ? (
        <div className="dashboard-lower-header">
          <div className="incident-chart-filter">
            <IncidentChartFilter />
          </div>
          <div className="dashboard-lower-container">
            <div>
              <IncidentList />
            </div>

            {/* Charts */}
            <div className={classes.root}>
              <Grid container spacing={0}>
                {[
                  { key: "bar", ref: barRef },
                  { key: "line", ref: lineRef },
                  { key: "donut", ref: donutRef },
                  { key: "radar", ref: radarRef },
                ].map(({ key, ref }) => (
                  <Grid
                    item
                    xs={expandedChart ? (expandedChart === key ? 12 : 0) : 6}
                    key={key}
                    style={{
                      display:
                        expandedChart && expandedChart !== key
                          ? "none"
                          : "block",
                    }}
                  >
                    <Paper
                      className={classes.paper}
                      style={{ position: "relative" }}
                    >
                      <div
                        className={`chart-card ${
                          expandedChart === key ? "expanded" : ""
                        }`}
                      >
                        {/* Title + Toggle bar only for radar */}
                        {key === "radar" && (
                          <div className="incidentRadarChart">
                            <span className="incident-radar-chart-title">
                              Incident by Detection Methods
                            </span>
                            <div
                              className={`custom-toggle ${
                                !excludeSIEM ? "" : "checked"
                              }`}
                              title="Exclude SIEM"
                              onClick={() => setExcludeSIEM((prev) => !prev)}
                            >
                              <div className="toggle-knob"></div>
                            </div>
                          </div>
                        )}

                        <div ref={ref} className="chart" />

                        {/* Fullscreen toggle icon */}
                        <div
                          className="fullscreen-icon"
                          onClick={() => toggleExpand(key)}
                          title={`Toggle ${
                            expandedChart === key ? "exit" : "fullscreen"
                          }`}
                        >
                          {expandedChart === key ? (
                            <FullscreenExitIcon fontSize="small" />
                          ) : (
                            <FullscreenIcon fontSize="small" />
                          )}
                        </div>
                      </div>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </div>
          </div>
          <JiraIncidentTable />
        </div>
      ) : (
        <div className="filter-table-container riskPilot">
          <div className="RRContainer">
            <img src={noDataAvailableSysguard} alt="no Data icon" />
            <div className="noDataFlexContainer">
              <p className="createRRText">{NO_DATA_TO_SHOW_RP}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
